import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DEFAULT_ROULETTE_PRIZES } from "./normalizer";

const DEFAULT_SPIN_DURATION_MS = 3600;
const MIN_FULL_TURNS = 6;
const EXTRA_FULL_TURNS = 3;

const getSafePrizes = (prizes) =>
  Array.isArray(prizes) && prizes.length > 0 ? prizes : DEFAULT_ROULETTE_PRIZES;

export const selectRoulettePrize = (prizes, random = Math.random) => {
  const safePrizes = getSafePrizes(prizes);
  const index = Math.min(
    safePrizes.length - 1,
    Math.floor(random() * safePrizes.length)
  );

  return safePrizes[index];
};

export const getRouletteRotationForPrize = (
  prizeAngle,
  fullTurns = MIN_FULL_TURNS
) => fullTurns * 360 - prizeAngle;

export default function MarketingRouletteOverlay({ item, onClose }) {
  const { creative } = item;
  const [canClose, setCanClose] = useState(
    creative.showCloseAfterSeconds <= 0
  );
  const [rotation, setRotation] = useState(0);
  const [isSpinning, setIsSpinning] = useState(false);
  const [result, setResult] = useState(null);
  const spinTimerRef = useRef(null);
  const durationMs = Number(creative.spinDurationMs) || DEFAULT_SPIN_DURATION_MS;

  const prizes = useMemo(() => getSafePrizes(creative.prizes), [
    creative.prizes,
  ]);

  useEffect(() => {
    if (creative.showCloseAfterSeconds <= 0) return undefined;

    const timer = setTimeout(
      () => setCanClose(true),
      creative.showCloseAfterSeconds * 1000
    );

    return () => clearTimeout(timer);
  }, [creative.showCloseAfterSeconds]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  useEffect(
    () => () => {
      if (spinTimerRef.current) {
        clearTimeout(spinTimerRef.current);
      }
    },
    []
  );

  const requestClose = useCallback(
    (reason) => {
      if (!creative.dismissible || !canClose) return;
      onClose(reason);
    },
    [canClose, creative.dismissible, onClose]
  );

  const closeAfterResult = useCallback(() => {
    if (!result) return;
    onClose("roulette_result_click");
  }, [onClose, result]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        requestClose("escape");
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [requestClose]);

  const startSpin = useCallback(() => {
    if (isSpinning || result) return;

    const prize = selectRoulettePrize(prizes);
    const fullTurns =
      MIN_FULL_TURNS + Math.floor(Math.random() * EXTRA_FULL_TURNS);

    setIsSpinning(true);
    setRotation(getRouletteRotationForPrize(prize.angle, fullTurns));

    spinTimerRef.current = setTimeout(() => {
      setIsSpinning(false);
      setResult(prize);
    }, durationMs);
  }, [durationMs, isSpinning, prizes, result]);

  return (
    <div
      className="marketing-overlay marketing-roulette-overlay"
      role="dialog"
      aria-modal="true"
      aria-label={creative.asset.alt || "Ruleta"}
    >
      <button
        type="button"
        className="marketing-overlay-backdrop"
        aria-label="Cerrar anuncio"
        disabled={!creative.dismissible || !canClose}
        onClick={() => requestClose("backdrop")}
      />
      <div className="marketing-roulette-panel">
        <div className="marketing-roulette-stage">
          <img
            className="marketing-roulette-disc"
            src={creative.asset.discSrc}
            alt=""
            aria-hidden="true"
            style={{
              transform: `rotate(${rotation}deg)`,
              transitionDuration: isSpinning ? `${durationMs}ms` : "0ms",
            }}
          />
          <img
            className="marketing-roulette-front"
            src={creative.asset.src}
            alt={creative.asset.alt}
          />
          <button
            type="button"
            className="marketing-roulette-spin-hitarea"
            aria-label="Girar ruleta"
            disabled={isSpinning || !!result}
            onClick={startSpin}
          />
          {result ? (
            <div className="marketing-roulette-result" role="status">
              Ganaste {result.label}
            </div>
          ) : null}
          {creative.dismissible && canClose ? (
            <button
              type="button"
              className="marketing-roulette-close-hitarea"
              aria-label="Cerrar anuncio"
              onClick={() => requestClose("close_button")}
            />
          ) : null}
        </div>
        {result ? (
          <button
            type="button"
            className="marketing-roulette-result-hitarea"
            aria-label="Cerrar anuncio"
            onClick={closeAfterResult}
          />
        ) : null}
      </div>
    </div>
  );
}
